import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Header from './components/Header';
import Footer from './components/Footer';
import PatrioticBackground from './components/PatrioticBackground';
import './App.css';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AppContainer = styled.div`
  text-align: center;
  min-height: 100vh;
  background: linear-gradient(45deg, #000000, #1a1a1a, #2b2b2b);
  background-size: 400% 400%;
  animation: ${gradientAnimation} 15s ease infinite;
  color: #fff;
  font-family: 'Oswald', sans-serif;
  position: relative;
  overflow: hidden;
  padding-top: 70px;

  @media (max-width: 768px) {
    padding-top: 60px;
  }

  @media (max-width: 480px) {
    padding-top: 50px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/american-flag-bg.png') repeat;
    opacity: 0.05;
    pointer-events: none;
  }
`;

const MainContent = styled.main`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  min-height: calc(100vh - 70px);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
    min-height: calc(100vh - 60px);
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    min-height: calc(100vh - 50px);
    gap: 1rem;
  }
`;

const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-15px) rotate(-1deg); }
  75% { transform: translateY(5px) rotate(1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 30px rgba(255, 0, 0, 0.3); }
  25% { box-shadow: 0 0 50px rgba(255, 0, 0, 0.5), 0 0 70px rgba(255, 255, 255, 0.2); }
  50% { box-shadow: 0 0 40px rgba(255, 0, 0, 0.4), 0 0 20px rgba(255, 255, 255, 0.1); }
  75% { box-shadow: 0 0 50px rgba(255, 0, 0, 0.5), 0 0 70px rgba(255, 255, 255, 0.2); }
  100% { box-shadow: 0 0 30px rgba(255, 0, 0, 0.3); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.5; }
`;

const wiggle = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(-5deg); }
  75% { transform: rotate(5deg); }
  100% { transform: rotate(0deg); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const shake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-5deg); }
  75% { transform: rotate(5deg); }
`;

const glowPulse = keyframes`
  0%, 100% { text-shadow: 0 0 10px #ff0000, 0 0 20px #ff0000, 0 0 30px #ff0000; }
  50% { text-shadow: 0 0 20px #ff0000, 0 0 30px #ff0000, 0 0 40px #ff0000; }
`;

const HeroSection = styled.section`
  margin: 0 auto 3rem;
  position: relative;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1000px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 30px;
  border: 1px solid rgba(255, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 0 auto 2rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin: 0 auto 1.5rem;
    border-radius: 20px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(255,0,0,0.1) 0%, transparent 70%);
    animation: ${pulse} 4s ease-in-out infinite;
    pointer-events: none;
    border-radius: inherit;
  }

  .hero-image-container {
    position: relative;
    display: inline-block;
    margin-bottom: 2rem;
    width: 100%;
    max-width: 600px;
    
    @media (max-width: 768px) {
      margin-bottom: 1.5rem;
      max-width: 90%;
    }
    
    @media (max-width: 480px) {
      margin-bottom: 1rem;
      max-width: 100%;
    }
    
    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      border: 1px solid rgba(255, 0, 0, 0.3);
      border-radius: 25px;
      z-index: -1;

      @media (max-width: 480px) {
        top: -5px;
        left: -5px;
        right: -5px;
        bottom: -5px;
        border-radius: 15px;
      }
    }
  }

  .hero-image {
    width: 100%;
    height: auto;
    object-fit: contain;
    border-radius: 20px;
    margin: 0;
    max-height: 400px;
    
    @media (max-width: 768px) {
      border-radius: 15px;
      max-height: 350px;
    }

    @media (max-width: 480px) {
      border-radius: 10px;
      max-height: 300px;
    }

    animation: ${float} 6s ease-in-out infinite,
              ${glow} 4s ease-in-out infinite;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
    }
  }

  h1 {
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 900;
    margin: 1rem 0;
    padding: 0;
    width: 100%;
    
    @media (max-width: 768px) {
      margin: 0.75rem 0;
    }

    @media (max-width: 480px) {
      margin: 0.5rem 0;
    }

    background: linear-gradient(180deg, 
      #ffffff 0%,
      #f8f8f8 40%,
      #ff0000 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase;
    font-family: 'Oswald', sans-serif;
    line-height: 1.2;
    text-align: center;
    letter-spacing: clamp(2px, 1vw, 6px);
    position: relative;
    z-index: 2;
    display: inline-block;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4));

    .symbol {
      font-size: clamp(0.35em, 2vw, 0.45em);
      font-weight: 800;
      display: block;
      letter-spacing: clamp(2px, 1vw, 8px);
      margin-top: clamp(0.4rem, 2vw, 0.8rem);
      background: linear-gradient(90deg, 
        #ff0000 0%,
        #ff4444 50%,
        #ff0000 100%
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: shimmer 2s infinite linear;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 60%;
        height: 2px;
        background: linear-gradient(90deg,
          transparent 0%,
          rgba(255, 0, 0, 0.5) 50%,
          transparent 100%
        );
      }
    }
  }

  p {
    font-size: 1.5rem;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin: 1rem 0;
    width: 100%;
    max-width: 600px;
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
      letter-spacing: 1px;
      padding: 0 1rem;
    }

    @media (max-width: 480px) {
      font-size: 1rem;
      padding: 0.5rem 1rem;
      margin: 0.75rem 0;
      max-width: 100%;
    }

    font-weight: 500;
    position: relative;
    display: inline-block;
    font-family: 'Oswald', sans-serif;
    padding: 1rem 2rem;
    background: rgba(255, 0, 0, 0.1);
    border: 1px solid rgba(255, 0, 0, 0.3);
    backdrop-filter: blur(5px);
    border-radius: 4px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      background: rgba(255, 0, 0, 0.15);
      border-color: rgba(255, 0, 0, 0.4);
      box-shadow: 0 4px 15px rgba(255, 0, 0, 0.2);
    }
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 20px;
      height: 20px;
      border: 2px solid #ff0000;
      opacity: 0.5;

      @media (max-width: 480px) {
        width: 15px;
        height: 15px;
      }
    }

    &::before {
      top: 0;
      left: 0;
      border-right: none;
      border-bottom: none;
    }

    &::after {
      bottom: 0;
      right: 0;
      border-left: none;
      border-top: none;
    }
  }
`;

const FunElement = styled.div`
  position: absolute;
  pointer-events: none;
  z-index: 2;
  opacity: 0.7;
  animation: ${float} 3s ease-in-out infinite;
  
  &.wall {
    font-size: 2rem;
    color: #ff0000;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
    animation: ${wiggle} 2s ease-in-out infinite;
  }
  
  &.tweet {
    font-size: 1.2rem;
    color: #1DA1F2;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
    animation: ${bounce} 1.5s ease-in-out infinite;
  }
`;

const TokenInfo = styled.section`
  margin: 0 0 3rem;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 30px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 3rem;

  @media (max-width: 768px) {
    margin: 0 0 2rem;
    padding: 1.5rem;
    gap: 2rem;
    border-radius: 25px;
  }

  @media (max-width: 480px) {
    margin: 0 0 1.5rem;
    padding: 1rem;
    gap: 1.5rem;
    border-radius: 20px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent, rgba(255, 0, 0, 0.05), transparent);
    pointer-events: none;
  }

  .info-group {
    text-align: center;
    position: relative;
    z-index: 1;
  }

  h2 {
    color: #fff;
    margin-bottom: 1.5rem;
    font-size: 2.2rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: linear-gradient(45deg, #ff0000, #ff4444);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    padding: 0.5rem 1rem;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 50%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #ff0000, transparent);
    }

    @media (max-width: 768px) {
      font-size: 1.8rem;
      margin-bottom: 1.25rem;
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }
  }

  .contract-address {
    font-size: 1rem;
    color: #fff;
    font-family: monospace;
    background: rgba(0, 0, 0, 0.6);
    padding: 1.25rem;
    border-radius: 15px;
    display: inline-block;
    position: relative;
    transition: all 0.3s ease;
    word-break: break-all;
    cursor: pointer;
    border: 1px solid rgba(255, 0, 0, 0.2);
    max-width: 600px;
    width: 100%;

    &:hover {
      transform: translateY(-2px);
      background: rgba(0, 0, 0, 0.7);
      border-color: rgba(255, 0, 0, 0.4);
      box-shadow: 0 5px 15px rgba(255, 0, 0, 0.2);
    }

    &::before {
      content: '🔗';
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      font-size: 1.2rem;
      opacity: 0.7;
      transition: all 0.3s ease;
    }

    &:hover::before {
      opacity: 1;
      transform: translateY(-50%) scale(1.1);
    }

    @media (max-width: 480px) {
      font-size: 0.85rem;
      padding: 1rem;
    }
  }

  .price-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;

    @media (max-width: 768px) {
      gap: 1rem;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }

    @media (max-width: 480px) {
      gap: 0.75rem;
      margin: 1.5rem 0;
    }
  }

  .social-links {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      gap: 0.75rem;
    }

    @media (max-width: 480px) {
      gap: 0.5rem;
    }
  }

  .view-chart, .telegram-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border-radius: 12px;
    color: #fff;
    font-size: 1rem;
    text-transform: uppercase;
    transition: all 0.3s ease;
    text-decoration: none;
    min-width: 160px;
    font-weight: 500;
    letter-spacing: 0.5px;
    backdrop-filter: blur(5px);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 768px) {
      min-width: 140px;
      padding: 0.6rem 1rem;
    }

    @media (max-width: 480px) {
      min-width: auto;
      flex: 1;
      padding: 0.5rem 0.75rem;
      font-size: 0.9rem;
    }
  }

  .view-chart {
    background: linear-gradient(45deg, rgba(255, 0, 0, 0.2), rgba(255, 0, 0, 0.3));
    border: 1px solid rgba(255, 0, 0, 0.3);

    &:hover {
      background: linear-gradient(45deg, rgba(255, 0, 0, 0.3), rgba(255, 0, 0, 0.4));
      border-color: rgba(255, 0, 0, 0.5);
    }
  }

  .telegram-link {
    background: linear-gradient(45deg, rgba(0, 136, 204, 0.2), rgba(0, 136, 204, 0.3));
    border: 1px solid rgba(0, 136, 204, 0.3);

    &:hover {
      background: linear-gradient(45deg, rgba(0, 136, 204, 0.3), rgba(0, 136, 204, 0.4));
      border-color: rgba(0, 136, 204, 0.5);
    }
  }
`;

const StarsContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
`;

const Star = styled.div`
  position: absolute;
  background: #fff;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  opacity: ${props => props.opacity};
  transform: translateY(${props => props.position}px);
  left: ${props => props.left}%;
  animation: fall ${props => props.duration}s linear infinite;

  @keyframes fall {
    to {
      transform: translateY(100vh);
    }
  }
`;

const PriceMovement = styled.div`
  position: absolute;
  top: -20px;
  right: 10px;
  font-size: 1.2rem;
  font-weight: bold;
  color: ${props => props.isUp ? '#00ff00' : '#ff0000'};
  animation: ${float} 2s ease-in-out infinite;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
`;

const BuyButton = styled.a`
  display: inline-block;
  padding: 1rem 2rem;
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #fff;
  background: linear-gradient(45deg, #ff0000, #ff4444);
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  margin-top: 2rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(255, 0, 0, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 0, 0, 0.4);
    animation: ${shake} 0.5s ease-in-out infinite;
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 60%);
    transform: rotate(45deg);
    animation: ${pulse} 2s linear infinite;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0.8rem 1.6rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.7rem 1.4rem;
  }
`;

const PriceStats = styled.div`
  position: relative;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 15px;
  border: 1px solid rgba(255, 0, 0, 0.2);
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255, 0, 0, 0.4);
  }

  .stat-value {
    font-size: 1.8rem;
    font-weight: bold;
    color: #fff;
    margin: 0.5rem 0;
    animation: ${glowPulse} 2s infinite;
  }

  .stat-label {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
  }
`;

const QuickLinks = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
  padding: 1rem;

  @media (max-width: 768px) {
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
    padding: 0.5rem;
  }
`;

const QuickLinkCard = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 0, 0, 0.2);
  border-radius: 15px;
  transition: all 0.3s ease;
  text-decoration: none;
  color: #fff;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 0, 0, 0.1), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255, 0, 0, 0.4);
    box-shadow: 0 5px 15px rgba(255, 0, 0, 0.2);

    &::before {
      transform: translateX(100%);
    }

    .icon {
      transform: scale(1.2);
    }
  }

  .icon {
    font-size: 2rem;
    transition: transform 0.3s ease;
  }

  .content {
    flex: 1;

    h3 {
      font-size: 1.2rem;
      margin: 0 0 0.5rem;
      color: #ff0000;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    p {
      font-size: 0.9rem;
      margin: 0;
      color: rgba(255, 255, 255, 0.8);
      line-height: 1.4;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem;

    .icon {
      font-size: 1.5rem;
    }

    .content {
      h3 {
        font-size: 1rem;
      }

      p {
        font-size: 0.8rem;
      }
    }
  }
`;

function App() {
  const [stars, setStars] = useState([]);
  const [funElements, setFunElements] = useState([]);
  const [priceData, setPriceData] = useState(null);

  useEffect(() => {
    const generateStars = () => {
      const newStars = [];
      for (let i = 0; i < 50; i++) {
        newStars.push({
          id: i,
          left: Math.random() * 100,
          opacity: Math.random(),
          duration: 3 + Math.random() * 7,
          position: -Math.random() * 100
        });
      }
      setStars(newStars);
    };

    const generateFunElements = () => {
      const elements = [
        { id: 1, text: '🦅', class: 'wall', top: '10%', left: '5%' },
        { id: 2, text: '🇺🇸', class: 'tweet', top: '20%', right: '10%' },
        { id: 3, text: 'MAGA', class: 'wall', bottom: '30%', left: '15%' },
        { id: 4, text: '🚀', class: 'tweet', bottom: '20%', right: '5%' },
        { id: 5, text: '💪', class: 'wall', top: '40%', right: '15%' },
        { id: 6, text: '🇺🇸', class: 'tweet', bottom: '40%', left: '10%' }
      ];
      setFunElements(elements);
    };

    const fetchPriceData = async () => {
      try {
        const response = await fetch('https://api.dexscreener.com/latest/dex/pairs/solana/878fuwoov1rw4mcewwphlgafdntg9jxzwvuadkrzhzky');
        const data = await response.json();
        if (data.pair) {
          setPriceData(data.pair);
        }
      } catch (error) {
        console.error('Error fetching price data:', error);
      }
    };

    generateStars();
    generateFunElements();
    fetchPriceData();
    const interval = setInterval(fetchPriceData, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Contract address copied to clipboard!');
  };

  return (
    <AppContainer>
      <PatrioticBackground />
      <StarsContainer>
        {stars.map(star => (
          <Star key={star.id} {...star} />
        ))}
      </StarsContainer>
      {funElements.map(element => (
        <FunElement
          key={element.id}
          className={element.class}
          style={{
            top: element.top,
            bottom: element.bottom,
            left: element.left,
            right: element.right
          }}
        >
          {element.text}
        </FunElement>
      ))}
      <Header />
      <MainContent>
        <HeroSection>
          <div className="hero-image-container">
            <img src="/tpepe.png" alt="Trump Pepe" className="hero-image" />
          </div>
          <h1>
            TRUMP PEPE
            <span className="symbol">$TETE</span>
          </h1>
          <p>The Memecoin That Makes Solana Great Again</p>
        </HeroSection>

        <QuickLinks>
          <QuickLinkCard href="https://raydium.io/swap/?inputCurrency=sol&outputCurrency=6iYhXiSSsYxgbgGSRatENrPm12AA68ruZvD39UTRFjvv&fixed=in" target="_blank" rel="noopener noreferrer">
            <div className="icon">💰</div>
            <div className="content">
              <h3>Buy $TETE</h3>
              <p>Trade on Raydium DEX</p>
            </div>
          </QuickLinkCard>
          <QuickLinkCard href="https://t.me/TETESOLCTO" target="_blank" rel="noopener noreferrer">
            <div className="icon">📢</div>
            <div className="content">
              <h3>Official Channel</h3>
              <p>Join our Telegram community</p>
            </div>
          </QuickLinkCard>
          <QuickLinkCard href="https://t.me/TheTrumpPepe_Bot" target="_blank" rel="noopener noreferrer">
            <div className="icon">🧠</div>
            <div className="content">
              <h3>TETE AI</h3>
              <p>Chat with Trump AI Bot</p>
            </div>
          </QuickLinkCard>
          <QuickLinkCard href="https://dexscreener.com/solana/878fuwoov1rw4mcewwphlgafdntg9jxzwvuadkrzhzky" target="_blank" rel="noopener noreferrer">
            <div className="icon">📈</div>
            <div className="content">
              <h3>Live Chart</h3>
              <p>Track price on DexScreener</p>
            </div>
          </QuickLinkCard>
        </QuickLinks>

        <TokenInfo>
          <div className="info-group">
            <h2>Contract Address</h2>
            <div 
              className="contract-address"
              onClick={() => copyToClipboard('6iYhXiSSsYxgbgGSRatENrPm12AA68ruZvD39UTRFjvv')}
              title="Click to copy"
            >
              6iYhXiSSsYxgbgGSRatENrPm12AA68ruZvD39UTRFjvv
            </div>
          </div>
          
          <div className="info-group">
            <h2>Live Price Data</h2>
            <div className="price-container">
              <PriceStats>
                <div className="stat-label">Current Price</div>
                <div className="stat-value">
                  {priceData ? `$${parseFloat(priceData.priceUsd).toFixed(8)}` : 'Loading...'}
                </div>
                {priceData && (
                  <PriceMovement isUp={parseFloat(priceData.priceChange.h24) > 0}>
                    {parseFloat(priceData.priceChange.h24) > 0 ? '🚀' : '📉'}
                  </PriceMovement>
                )}
              </PriceStats>
              <PriceStats>
                <div className="stat-label">24h Change</div>
                <div className="stat-value" style={{ color: priceData && parseFloat(priceData.priceChange.h24) > 0 ? '#00ff00' : '#ff0000' }}>
                  {priceData ? `${parseFloat(priceData.priceChange.h24).toFixed(2)}%` : 'Loading...'}
                </div>
              </PriceStats>
              <PriceStats>
                <div className="stat-label">24h Volume</div>
                <div className="stat-value">
                  {priceData ? `$${parseInt(priceData.volume.h24).toLocaleString()}` : 'Loading...'}
                </div>
              </PriceStats>
            </div>
            <BuyButton 
              href="https://raydium.io/swap/?inputCurrency=sol&outputCurrency=6iYhXiSSsYxgbgGSRatENrPm12AA68ruZvD39UTRFjvv&fixed=in" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Buy Now 🚀
            </BuyButton>
            <div className="social-links">
              <a 
                href="https://dexscreener.com/solana/878fuwoov1rw4mcewwphlgafdntg9jxzwvuadkrzhzky" 
                target="_blank" 
                rel="noopener noreferrer"
                className="view-chart"
              >
                Chart 📈
              </a>
              <a 
                href="https://t.me/TETESOLCTO" 
                target="_blank" 
                rel="noopener noreferrer"
                className="telegram-link"
              >
                Channel 📢
              </a>
              <a 
                href="https://t.me/TheTrumpPepe_Bot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="telegram-link"
              >
                TETE AI 🧠
              </a>
            </div>
          </div>
        </TokenInfo>
      </MainContent>
      <Footer />
    </AppContainer>
  );
}

export default App;
